import { Component, h, Host, Prop, Element, Fragment } from "@stencil/core";
import { CardShell } from "../cards/card-shell/card-shell";

import { register } from "swiper/element/bundle";
import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";
import lottie, { AnimationItem } from "lottie-web";
import scrollAnimation from "./assets/mouse_scrolling.animation.json";
import { prefersReducedMotion } from "../../utilities/user";
import { parseTrackingAttributes } from "../../utilities/card";

register();

export type HeroAreaCard = {
  heroTitle?: string;
  heroTitleColor?: "white" | "navy";
  buttonText?: string;
  buttonEnabled?: boolean;
  buttonAnchor?: string;
  buttonIcon?: string;
  trackingEnabled?: boolean;
  trackingAttributes?: string;
  heroTitleSize?: string;
  cardData: CardShell;
};

@Component({
  tag: "scs-hero-area",
  styleUrls: ["hero-area.scss"],
  shadow: true,
  assetsDirs: ["assets"],
})
export class HeroArea {
  @Prop() public cardData: string = "[]";

  @Element() private hostEl!: HTMLElement;

  private swiperEl?: HTMLElement & { swiper?: any };
  private cards: HeroAreaCard[] = [];

  private lottieContainers: Array<HTMLDivElement | undefined> = [];
  private lottieAnimations: AnimationItem[] = [];

  private readonly trackByEvent: (event: Event) => void;
  private readonly adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
  }

  private parseCards(json: string): HeroAreaCard[] {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  public async componentWillLoad(): Promise<void> {
    this.cards = this.parseCards(this.cardData);
  }

  private getGradientDirection(card: HeroAreaCard) {
    return card.cardData?.gradientDirection || "top";
  }

  private playActiveContentAnimation = () => {
    const swiper = this.swiperEl?.swiper;
    if (!this.swiperEl || !swiper) return;

    const all = this.swiperEl.querySelectorAll(".text-button-content");
    all.forEach((el: Element) =>
      (el as HTMLElement).classList.remove("ha-animate"),
    );

    const activeSlideEl = swiper.slides?.[swiper.activeIndex] as
      | HTMLElement
      | undefined;
    const content = activeSlideEl?.querySelector(
      ".text-button-content",
    ) as HTMLElement | null;
    if (!content) return;

    content.classList.remove("ha-animate");
    void content.offsetWidth;
    content.classList.add("ha-animate");
  };

  private setProgressFraction(value: number) {
    const clamped = Math.min(Math.max(value, 0), 1);
    this.hostEl.style.setProperty("--ha-progress-fraction", `${clamped}`);
  }

  private getCardShellClasses(card: HeroAreaCard): string {
    return [
      "heroAreaShell",
      card?.heroTitleColor === "white" && "ha-title-white",
    ]
      .filter(Boolean)
      .join(" ");
  }

  private injectSwiperPaginationStyles(): void {
    const root = (this.swiperEl as any)?.shadowRoot as ShadowRoot | undefined;
    if (!root) return;

    const styleId = "ha-pagination-style";
    if (root.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .swiper-pagination-bullet.swiper-pagination-bullet-active {
        cursor: default !important;
        pointer-events: none !important;
      }
    `;
    root.appendChild(style);
  }

  private renderTitle(card: HeroAreaCard) {
    const raw = typeof card.heroTitle === "string" ? card.heroTitle : "";
    const title = raw.trim();
    if (!title) return null;

    const lines = title.split(/\r?\n/);
    const classes = [card.heroTitleSize ? card.heroTitleSize : "d1"]
      .filter(Boolean)
      .join(" ");
    return (
      <h2 class={classes || undefined}>
        {lines.map((line, i, arr) => (
          <Fragment>
            {line}
            {i < arr.length - 1 ? <br /> : null}
          </Fragment>
        ))}
      </h2>
    );
  }

  render() {
    return (
      <Host class="ha-wrapper">
        <swiper-container
          ref={(el: any) => (this.swiperEl = el)}
          init="false"
          class="hero-swiper"
          aria-roledescription="carousel"
        >
          {this.cards.map((card, idx) => (
            <swiper-slide
              key={idx}
              role="group"
              aria-roledescription="slide"
              aria-label={`${idx + 1} / ${this.cards.length}`}
            >
              <scs-card-shell
                class={this.getCardShellClasses(card)}
                gradient-direction={this.getGradientDirection(card)}
                background-image={JSON.stringify(
                  card.cardData?.backgroundImage,
                )}
                background-video={JSON.stringify(
                  card.cardData?.backgroundVideo,
                )}
                background-lottie={JSON.stringify(
                  card.cardData?.backgroundLottie,
                )}
                sdx-gradient={card.cardData?.sdxGradient}
                background-color={card.cardData?.backgroundColor}
              >
                <div class="hero-content">
                  <div class="text-button-content">
                    {this.renderTitle(card)}

                    {card.buttonEnabled ? (
                      <sdx-button-group>
                        <sdx-button
                          label={card.buttonText?.trim()}
                          href={card.buttonAnchor}
                          icon-name={card.buttonIcon}
                          {...parseTrackingAttributes(
                            card.trackingAttributes || "{}",
                          )}
                          onClick={(ev: Event) => {
                            if (card.trackingEnabled) {
                              this.trackByEvent(ev);
                            }
                          }}
                        ></sdx-button>
                      </sdx-button-group>
                    ) : (
                      <figure
                        class="mouse-scroll-lottie"
                        ref={el =>
                          (this.lottieContainers[idx] =
                            (el as HTMLDivElement) ?? undefined)
                        }
                      />
                    )}
                  </div>
                </div>
              </scs-card-shell>
            </swiper-slide>
          ))}
        </swiper-container>
      </Host>
    );
  }

  componentDidLoad() {
    if (!this.swiperEl) return;

    Object.assign(this.swiperEl, {
      slidesPerView: 1,
      speed: 500,
      loop: true,
      effect: "fade",
      fadeEffect: { crossFade: true },
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
        stopOnLastSlide: false,
        waitForTransition: false,
      },
      pagination: { clickable: true },
      keyboard: { enabled: false },
      a11y: { enabled: true },
      allowTouchMove: false,
      simulateTouch: false,
    });

    (this.swiperEl as any).initialize();

    this.injectSwiperPaginationStyles();

    const swiper = (this.swiperEl as any).swiper;
    if (!swiper) return;

    swiper.on("afterInit", () => {
      this.setProgressFraction(0);
      requestAnimationFrame(() => this.playActiveContentAnimation());
    });

    const getDelay = () =>
      swiper.params?.autoplay?.delay ?? swiper.autoplay?.params?.delay ?? 3000;

    swiper.on(
      "autoplayTimeLeft",
      (_s: any, timeLeft: number, progress: number) => {
        const delay = Number(getDelay()) || 3000;
        const elapsedFractionFromTimeLeft = 1 - timeLeft / delay;
        const normalized = Math.max(
          0,
          Math.min(
            1,
            isFinite(elapsedFractionFromTimeLeft)
              ? elapsedFractionFromTimeLeft
              : 1 - (progress ?? 0),
          ),
        );
        this.setProgressFraction(normalized);
      },
    );

    swiper.on("slideChangeTransitionStart", () => {
      this.setProgressFraction(0);
      this.playActiveContentAnimation();
    });

    swiper.on("paginationUpdate", () => {
      this.setProgressFraction(0);
    });

    const autoplay = !prefersReducedMotion();

    this.lottieAnimations = this.lottieContainers
      .filter((el): el is HTMLDivElement => !!el)
      .map(el =>
        lottie.loadAnimation({
          container: el,
          renderer: "svg",
          loop: true,
          autoplay,
          animationData: scrollAnimation,
        }),
      );
  }

  disconnectedCallback() {
    const swiper = this.swiperEl?.swiper;
    if (swiper) {
      swiper.off("afterInit");
      swiper.off("autoplayTimeLeft");
      swiper.off("slideChangeTransitionStart");
      swiper.off("paginationUpdate");
    }

    this.lottieAnimations.forEach(a => {
      try {
        a.destroy();
      } catch (e) {
        console.warn("Exception while destroying animations", e);
      }
    });
    this.lottieAnimations = [];
    this.lottieContainers = [];
  }
}
